import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/images/delete-icon.svg";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiDateField,
  MuiIconButton,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
} from "../../../../../../components";
import MuiChipWithPopover from "../../../../../../components/chipWithPopover/MuiChipWithPopover";
import { StatusBadge } from "../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import {
  compareDates,
  convertToUserFormat,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import {
  SelectedParticipantResultType,
  SearchParticipantResultFormKey as selectedParticipantResultFormKey,
} from "../../../../modelsAndSchemas/Models";
import { bulkActivitySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { SelectedParticipantResultWrapper } from "./SelectedParticipant.style";
import { LabelValue } from "../../../../../../helpers/types/common";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";

interface SelectedParticipantsResultProps {
  rows: SelectedParticipantResultType[];
  pageId: string;
  handleDelete?: (id: string) => void;
  currentStep: number;
  isSummaryPage?: boolean;
  isUnprocessed?: boolean;
}

export const SelectedParticipantResult: FC<SelectedParticipantsResultProps> = ({
  rows = [],
  pageId,
  handleDelete,
  currentStep,
  isSummaryPage = false,
  isUnprocessed,
}) => {
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SelectedParticipantResultType[]
  >([]);
  const [uniqueLineOfBusinesses, setUniqueLineOfBusinesses] = useState<
    LabelValue[]
  >([]);
  const [uniqueTemplates, setUniqueTemplates] = useState<LabelValue[]>([]);
  const [uniqueDeliveryCountries, setUniqueDeliveryCountries] = useState<
    LabelValue[]
  >([]);
  const [uniqueNextProgramClass, setUniqueNextProgramClass] = useState<
    LabelValue[]
  >([]);
  const [uniqueDeliveryOffice, setUniqueDeliveryOffice] = useState<
    LabelValue[]
  >([]);
  const [uniqueStatus, setUniqueStatus] = useState<LabelValue[]>([]);

  const getLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.selectedParticipants, key);
  };

  const getTableLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.summaryTable, key);
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const defaultSelectedParticipantResult = {
    [selectedParticipantResultFormKey.participantName]: "",
    [selectedParticipantResultFormKey.programName]: "",
    [selectedParticipantResultFormKey.companyName]: "",
    [selectedParticipantResultFormKey.programTemplateName]: "",
    [selectedParticipantResultFormKey.deliveryCountryName]: "",
    [selectedParticipantResultFormKey.deliveryOfficeName]: "",
    [selectedParticipantResultFormKey.status]: "",
    [selectedParticipantResultFormKey.lobName]: "",
    [selectedParticipantResultFormKey.nextProgramClassName]: "",
    [selectedParticipantResultFormKey.createdDate]: "",
    [selectedParticipantResultFormKey.notes]: "",
    [selectedParticipantResultFormKey.coach]: "",
  };

  const { reset, control, getValues, setValue } = useForm({
    mode: "all",
    defaultValues: defaultSelectedParticipantResult,
  });

  const selectedParticipantsFilters = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const localData: SelectedParticipantResultType[] = JSON.parse(
      JSON.stringify(rows),
    );

    const uniqueLineOfBusiness = Array.from(
      new Set(
        localData.map((item) =>
          isSummaryPage ? (item?.productLine ?? "") : item?.lobName,
        ),
      ),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueProgramTemplate = Array.from(
      new Set(localData.map((item) => item.programTemplateName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueNextProgClass = Array.from(
      new Set(
        localData.map((item) =>
          isSummaryPage
            ? (item?.nextProgramClass ?? "")
            : item?.nextProgramClassName,
        ),
      ),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueCountries = Array.from(
      new Set(
        localData.map((item) =>
          isSummaryPage
            ? (item?.deliveryCountry ?? "")
            : item.deliveryCountryName,
        ),
      ),
    ).map((country) => ({ value: country, label: country }));

    setUniqueTemplates(uniqueProgramTemplate);
    setUniqueNextProgramClass(uniqueNextProgClass);
    setUniqueDeliveryCountries(uniqueCountries);
    setUniqueLineOfBusinesses(uniqueLineOfBusiness);

    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: SelectedParticipantResultType[] = JSON.parse(
        JSON.stringify(rows),
      );

      if (isSummaryPage) {
        localData.forEach((data: SelectedParticipantResultType) => {
          data.statusKey = data.status;
          data.status = fetchValueByKeyOfDropdown(
            bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
            bulkActivitySiteCoreKeys.resultsStatusList,
            data.status,
          );
          data.deliveryOfficeName = data?.deliveryOffice ?? "";
          data.deliveryCountryName = data?.deliveryCountry ?? "";
          data.participantName = data?.participantName ?? "";
          data.isSelected = false;
          data.lobName = data.productLine ?? "";
          data.nextProgramClassName = data?.nextProgramClass ?? "";
        });
      }

      const uniqueServiceOffices = Array.from(
        new Set(localData.map((item) => item.deliveryOfficeName)),
      ).map((label) => ({ value: label, label: label }));

      const uniqueStatus = Array.from(
        new Set(localData.map((item) => item.status)),
      ).map((label) => ({ value: label, label: label }));

      setUniqueDeliveryOffice(uniqueServiceOffices);
      setUniqueStatus(uniqueStatus);
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const checkParticipantFilterMatch = (inputValue: string, rowItem: string) => {
    return isEmpty(inputValue?.toLowerCase())
      ? true
      : !isEmpty(rowItem) &&
          rowItem?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        programTemplateName,
        status,
        deliveryCountryName,
        deliveryOfficeName,
        createdDate,
        nextProgramClassName,
        participantName,
        programName,
        companyName,
        lobName,
        coach,
      } = getValues();

      const myData = allUpdatedRows.filter(
        (item: SelectedParticipantResultType) => {
          const programNameMatch = checkParticipantFilterMatch(
            programName,
            item.programName,
          );

          const participantNameMatch = checkParticipantFilterMatch(
            participantName,
            item?.participantName ?? "",
          );

          const companyNameMatch = checkParticipantFilterMatch(
            companyName,
            item.clientName,
          );

          const lineOfBusinessMatch = checkParticipantFilterMatch(
            lobName,
            item.lobName,
          );

          const programTemplateMatch = checkParticipantFilterMatch(
            programTemplateName,
            item.programTemplateName,
          );
          const nextProgramClassMatch = checkParticipantFilterMatch(
            nextProgramClassName,
            item.nextProgramClassName,
          );

          let isDateMatch = compareDates(
            createdDate ?? "",
            item?.createdDate ?? "",
            getValueOrBlank(userCountryDateFormat),
          );

          const statusAll = fetchValueByKeyOfDropdown(
            bulkActivitySiteCoreKeys.searchParticipantsKey,
            bulkActivitySiteCoreKeys.resultsStatusList,
            commonSiteCoreKeys.statusAll,
          );

          const statusMatch =
            status === "" ||
            status === statusAll ||
            item.status?.toLowerCase() === status?.toLowerCase();

          const deliveryCountryMatch = checkParticipantFilterMatch(
            deliveryCountryName,
            item.deliveryCountryName,
          );

          const deliveryOfficeMatch = checkParticipantFilterMatch(
            deliveryOfficeName,
            item.deliveryOfficeName,
          );

          const coachMatch =
            !coach ||
            item.coachesList?.some((c: string) =>
              c.toLowerCase().includes(coach.toLowerCase()),
            );

          return (
            programNameMatch &&
            participantNameMatch &&
            companyNameMatch &&
            lineOfBusinessMatch &&
            programTemplateMatch &&
            statusMatch &&
            deliveryCountryMatch &&
            deliveryOfficeMatch &&
            nextProgramClassMatch &&
            coachMatch &&
            isDateMatch
          );
        },
      );
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [selectedParticipantsFilters]);

  const columns = [
    {
      id: selectedParticipantResultFormKey.participantName,
      label: getLabel(
        bulkActivitySiteCoreKeys.selectedParticipantsParticipantName,
      ),
      className: "cell",
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3">{row.participantName}</MuiTypography>
      ),
    },
    {
      id: selectedParticipantResultFormKey.programName,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsProgramName),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.clientName,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsClient),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.programTemplateName,
      label: getLabel(
        bulkActivitySiteCoreKeys.selectedParticipantsProgramTemplate,
      ),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.nextProgramClassName,
      label: getLabel(bulkActivitySiteCoreKeys.nextProgramClass),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.status,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsStatus),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell",
      render: (row: RowProps) => (
        <StatusBadge status={row.statusKey} label={row.status} />
      ),
    },
    {
      id: selectedParticipantResultFormKey.lobName,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsProductLine),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },

    {
      id: selectedParticipantResultFormKey.deliveryCountryName,
      label: getLabel(
        bulkActivitySiteCoreKeys.selectedParticipantsDeliveryCountry,
      ),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.deliveryOfficeName,
      label: getLabel(
        bulkActivitySiteCoreKeys.selectedParticipantsDeliveryOffice,
      ),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: selectedParticipantResultFormKey.coachesList,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsCoach),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiChipWithPopover
          data={
            row.coachesList?.map((item: string) => ({
              label: item,
              value: item,
            })) as ChipOptionsDataType[]
          }
          id={"selected-coaches"}
          isEditable={false}
          headerLabel={getLabel(
            bulkActivitySiteCoreKeys.selectedParticipantsCoach,
          )}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
    },
    {
      id: selectedParticipantResultFormKey.createdDate,
      label: getLabel(bulkActivitySiteCoreKeys.selectedParticipantsCreatedDate),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => {
        return (
          <>{convertToUserFormat(row.createdDate, userCountryDateFormat)}</>
        );
      },
    },
    {
      id: "action",
      label: getLabel(bulkActivitySiteCoreKeys.actionHeader),
      className: "action",
      render: (row: RowProps) => {
        return (
          <MuiIconButton
            onClick={() => handleDelete?.(row.participantId)}
            data-testid={`${pageId}-delete-participant`}
            aria-label={commonARIALabel.delete}
          >
            <DeleteIcon />
          </MuiIconButton>
        );
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.participantName}
      name={selectedParticipantResultFormKey.participantName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${selectedParticipantResultFormKey.participantName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.programName}
      name={selectedParticipantResultFormKey.programName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${selectedParticipantResultFormKey.programName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <MuiBox
      className={"programName"}
      key={selectedParticipantResultFormKey.companyName}
    >
      <FormController
        control={control}
        name={selectedParticipantResultFormKey.companyName}
      >
        <MuiTextFieldWithSearchIcon
          id={`${pageId}-${selectedParticipantResultFormKey.companyName}`}
          placeholder={commonLabels.search}
          showCloseIcon
          maxLength={MAX_LENGTH_75}
          shouldTrimSpaces={false}
          requiredARIALabel={commonARIALabel.required}
          searchARIALabel={commonARIALabel.search}
        />
      </FormController>
    </MuiBox>,
    <FormController
      control={control}
      name={selectedParticipantResultFormKey.programTemplateName}
      key={selectedParticipantResultFormKey.programTemplateName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueTemplates}
        value=""
        id={`${pageId}-${selectedParticipantResultFormKey.programTemplateName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={selectedParticipantResultFormKey.nextProgramClassName}
      key={selectedParticipantResultFormKey.nextProgramClassName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueNextProgramClass}
        value=""
        id={`${pageId}-${selectedParticipantResultFormKey.nextProgramClassName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiBox
      className={"programName"}
      key={selectedParticipantResultFormKey.status}
    >
      <FormController
        control={control}
        name={selectedParticipantResultFormKey.status}
      >
        <MuiSelect
          className="status-container"
          label=""
          visibleDefaultSelectOption
          options={uniqueStatus}
          value=""
          id={`${pageId}-${selectedParticipantResultFormKey.status}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    </MuiBox>,
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.lobName}
      name={selectedParticipantResultFormKey.lobName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueLineOfBusinesses}
        value=""
        id={`${pageId}-${selectedParticipantResultFormKey.lobName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.deliveryCountryName}
      name={selectedParticipantResultFormKey.deliveryCountryName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueDeliveryCountries}
        value=""
        id={`${pageId}-${selectedParticipantResultFormKey.deliveryCountryName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        showSearch
        searchPlaceholder={commonLabels.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.deliveryOfficeName}
      name={selectedParticipantResultFormKey.deliveryOfficeName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueDeliveryOffice}
        value=""
        id={`${pageId}-${selectedParticipantResultFormKey.deliveryOfficeName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={selectedParticipantResultFormKey.coach}
      key={selectedParticipantResultFormKey.coach}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${selectedParticipantResultFormKey.coach}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={selectedParticipantResultFormKey.createdDate}
      name={selectedParticipantResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name={selectedParticipantResultFormKey.createdDate}
          clearable
          value={
            getValues(selectedParticipantResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(selectedParticipantResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              selectedParticipantResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>, //for action column
  ];

  const getHeaderForm = (isUnprocessed: boolean): React.ReactElement[] => {
    const clonedForm = [...tableHeaderForm];

    if (isSummaryPage) {
      clonedForm.splice(9, 1); // Remove coach column
    }

    if (isUnprocessed) {
      // Remove status and nextProgramClassName
      clonedForm.splice(5, 1); // status
      clonedForm.splice(4, 1); // nextProgramClassName

      // Insert empty column for failure reason at index 1
      clonedForm.splice(1, 0, <></>);
    }

    return clonedForm.filter(React.isValidElement);
  };

  const getColumns = (isUnprocessed: boolean) => {
    let updatedColumns = columns;

    if (isSummaryPage) {
      // removing coach column from summary page
      updatedColumns = updatedColumns.filter(
        (col) => col.id !== selectedParticipantResultFormKey.coachesList,
      );
    }

    if (isUnprocessed) {
      updatedColumns = updatedColumns.filter(
        (col) =>
          col.id !== selectedParticipantResultFormKey.nextProgramClassName &&
          col.id !== selectedParticipantResultFormKey.status,
      );

      const noteColumn = {
        id: selectedParticipantResultFormKey.notes, // Make sure this exists in your keys object
        label: getTableLabel(bulkActivitySiteCoreKeys.failureReason),
        className: "cell",
        render: (row: any) => (
          <MuiBox>
            <MuiNotesToolTip message={row?.failureReason} />
          </MuiBox>
        ),
      };

      // Insert "note" at second position
      updatedColumns = [
        updatedColumns[0],
        noteColumn,
        ...updatedColumns.slice(1),
      ];
    }

    return currentStep === 2 ? updatedColumns.slice(0, -1) : updatedColumns;
  };

  return (
    <SelectedParticipantResultWrapper
      data-testid={`${pageId}-activity-selected-participants`}
    >
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={getHeaderForm(isUnprocessed ?? false)}
        columns={getColumns(isUnprocessed ?? false)}
        tableResults={[...filteredResults]}
        noDataInApi={filteredResults.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        emptyMessageColSpan={8}
        isLastColumnSticky={currentStep === 1}
      />
    </SelectedParticipantResultWrapper>
  );
};
